import React, { useState } from 'react';
import { Grid, Collapse, IconButton, ButtonBase } from '@mui/material';
import { useAutoAnimate } from '@formkit/auto-animate/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { ExpandMoreOutlined, ExpandLessOutlined } from '@mui/icons-material';
import { Title } from '../../components/atoms/title';
import { Card } from './card';

import api from '../../services/api';
import { useUniversities } from '../../hooks/universities';

export function IssueList() {
  const [parent] = useAutoAnimate();
  const queryClient = useQueryClient();
  const { issues } = useUniversities();

  const archivedIssues = [
    ...(issues?.archived?.payment || []),
    ...(issues?.archived?.bug || []),
    ...(issues?.archived?.['info change'] || []),
  ];
  const [archivedOpen, setArchivedOpen] = useState(false);

  const [paymentOpen, setPaymentOpen] = useState(true);
  const [infoChangeOpen, setInfoChangeOpen] = useState(true);
  const [bugOpen, setBugOpen] = useState(true);

  const archiveIssueMutation = useMutation(
    async issue => {
      const { data } = await api.put(`/admin/issues/${issue.id}`, { status: 'archived' });
      return data;
    },
    {
      onError: (error, variables, context) => {
        toast(`Ocorreu um erro: ${error.message}`, { type: 'error' });
        if (context?.previousIssues) {
          queryClient.setQueryData(['issues'], context.previousIssues);
        }
      },

      onMutate: async issue => {
        await queryClient.cancelQueries({ queryKey: ['issues'] });

        const previousIssues = queryClient.getQueryData(['issues']);

        queryClient.setQueryData(['issues'], old => ({
          ...old,
          open: {
            ...old.open,
            [issue.type]: old.open[issue.type].filter(i => i.id !== issue.id),
          },
          archived: {
            ...old.archived,
            [issue.type]: [...(old.archived[issue.type] || []), issue],
          },
        }));

        return { previousIssues };
      },
      mutationKey: 'archiveIssue',
    }
  );

  const handleUpdateIssue = async issue => {
    await archiveIssueMutation.mutateAsync(issue);
  };

  return (
    <Grid container spacing={2}>
      <Grid item>
        <Title>Lista de Tickets</Title>
      </Grid>
      <Grid container item spacing={2} style={{ width: '100%' }}>
        <Grid item style={{ width: '100%' }}>
          <iframe
            title='Looker Studio Issues Charts'
            width='100%'
            height='850'
            src='https://lookerstudio.google.com/embed/reporting/d09a2765-9231-44fb-9137-362467dc64aa/page/p_lc6v1n3pmd'
            style={{ border: 30 }}
            allowFullScreen
            sandbox='allow-storage-access-by-user-activation allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox'
          />
        </Grid>
      </Grid>
      <Grid container item spacing={2}>
        <Grid item xs={12}>
          <ButtonBase
            style={{
              width: '100%',
              marginTop: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'left',
            }}
            onClick={() => setPaymentOpen(prev => !prev)}
          >
            <h3 style={{ fontWeight: 600, marginRight: '8px', fontFamily: 'Montserrat', fontSize: '19px' }}>
              Assinaturas e Pagamentos
            </h3>
            <IconButton aria-expanded={open} aria-label='show more' size='large' component='div'>
              {!paymentOpen ? <ExpandMoreOutlined /> : <ExpandLessOutlined />}
            </IconButton>
            <h4 style={{ fontWeight: 400, fontFamily: 'Montserrat', fontSize: '16px' }}>
              {issues?.open?.payment?.length}
            </h4>
          </ButtonBase>
          <Collapse in={paymentOpen}>
            <Grid container ref={parent}>
              {issues?.open?.payment?.map(issue => (
                <Card key={issue?.id} issue={issue} onArchiveClick={() => handleUpdateIssue(issue)} />
              ))}
            </Grid>
          </Collapse>
        </Grid>

        <Grid item xs={12}>
          <ButtonBase
            style={{
              width: '100%',
              marginTop: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'left',
            }}
            onClick={() => setInfoChangeOpen(prev => !prev)}
          >
            <h3 style={{ fontWeight: 600, marginRight: '8px', fontFamily: 'Montserrat', fontSize: '19px' }}>
              Mudança de Informação
            </h3>
            <IconButton aria-expanded={open} aria-label='show more' size='large' component='div'>
              {!infoChangeOpen ? <ExpandMoreOutlined /> : <ExpandLessOutlined />}
            </IconButton>
            <h4 style={{ fontWeight: 400, fontFamily: 'Montserrat', fontSize: '16px' }}>
              {issues?.open?.['info change']?.length}
            </h4>
          </ButtonBase>
          <Collapse in={infoChangeOpen}>
            <Grid container ref={parent}>
              {issues?.open?.['info change']?.map(issue => {
                return <Card key={issue.id} issue={issue} onArchiveClick={() => handleUpdateIssue(issue)} />;
              })}
            </Grid>
          </Collapse>
        </Grid>

        <Grid item xs={12}>
          <ButtonBase
            style={{
              width: '100%',
              marginTop: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'left',
            }}
            onClick={() => setBugOpen(prev => !prev)}
          >
            <h3 style={{ fontWeight: 600, marginRight: '8px', fontFamily: 'Montserrat', fontSize: '19px' }}>
              Outros Problemas
            </h3>
            <IconButton aria-expanded={open} aria-label='show more' size='large' component='div'>
              {!bugOpen ? <ExpandMoreOutlined /> : <ExpandLessOutlined />}
            </IconButton>
            <h4 style={{ fontWeight: 400, fontFamily: 'Montserrat', fontSize: '16px' }}>{issues?.open?.bug?.length}</h4>
          </ButtonBase>
          <Collapse in={bugOpen}>
            <Grid container ref={parent}>
              {issues?.open?.bug?.map(issue => (
                <Card key={issue.id} issue={issue} onArchiveClick={() => handleUpdateIssue(issue)} />
              ))}
            </Grid>
          </Collapse>
        </Grid>

        <Grid item xs={12}>
          <ButtonBase
            style={{
              width: '100%',
              marginTop: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'left',
            }}
            onClick={() => setArchivedOpen(!archivedOpen)}
          >
            <h3 style={{ fontWeight: 600, marginRight: '8px', fontFamily: 'Montserrat', fontSize: '19px' }}>
              Problemas arquivados
            </h3>
            <IconButton aria-expanded={open} aria-label='show more' size='large' component='div'>
              {!archivedOpen ? <ExpandMoreOutlined /> : <ExpandLessOutlined />}
            </IconButton>
          </ButtonBase>
          <Collapse in={archivedOpen}>
            <Grid container ref={parent}>
              {archivedIssues.map(issue => (
                <Card
                  key={issue.id}
                  issue={issue}
                  onArchiveClick={() => {
                    handleUpdateIssue(issue);
                  }}
                />
              ))}
            </Grid>
          </Collapse>
        </Grid>
      </Grid>
    </Grid>
  );
}
